import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useProducts, Product } from '../context/ProductsContext';
import PromoCountdownBanner from '../components/PromoCountdownBanner';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const getFinalPrice = (product: Product) =>
  product.discount && product.discount > 0
    ? product.price * (1 - product.discount / 100)
    : product.price;

const Home = () => {
  const { products } = useProducts();

  const [newCarouselProgress, setNewCarouselProgress] = React.useState(0);
  const [promoCarouselProgress, setPromoCarouselProgress] = React.useState(0);
  const [currentMannequinIndex, setCurrentMannequinIndex] = React.useState(0);
  const [mannequinImages, setMannequinImages] = React.useState<string[]>([]);
  const newProducts = products.filter((p) => p.isNew).slice(0, 4);
  const allPromoProducts = products.filter((p) => p.discount && p.discount > 0);
  const promoProducts = allPromoProducts.slice(0, 4);
  const categories = Array.from(new Set(products.map((p) => p.category)));

  const handleNewCarouselScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const maxScroll = target.scrollWidth - target.clientWidth;
    if (maxScroll <= 0) {
      setNewCarouselProgress(0);
    } else {
      setNewCarouselProgress(target.scrollLeft / maxScroll);
    }
  };

  const handlePromoCarouselScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const maxScroll = target.scrollWidth - target.clientWidth;
    if (maxScroll <= 0) {
      setPromoCarouselProgress(0);
    } else {
      setPromoCarouselProgress(target.scrollLeft / maxScroll);
    }
  };

  React.useEffect(() => {
    const maxImages = 20;
    let isCancelled = false;

    const loadImages = async () => {
      const loaded: (string | undefined)[] = [];

      await Promise.all(
        Array.from({ length: maxImages }, (_, index) => {
          const imgIndex = index + 1;
          const url = `/mannequins/look${imgIndex}.jpg`;

          return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
              loaded[index] = url;
              resolve();
            };
            img.onerror = () => {
              resolve();
            };
            img.src = url;
          });
        })
      );

      if (isCancelled) {
        return;
      }

      const existing = loaded.filter(
        (value): value is string => Boolean(value)
      );

      if (existing.length > 0) {
        setMannequinImages(existing);
      }
    };

    loadImages();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleNextMannequin = () => {
    if (mannequinImages.length === 0) return;
    setCurrentMannequinIndex(
      (prev) => (prev + 1) % mannequinImages.length
    );
  };

  const handlePrevMannequin = () => {
    if (mannequinImages.length === 0) return;
    setCurrentMannequinIndex(
      (prev) =>
        (prev - 1 + mannequinImages.length) % mannequinImages.length
    );
  };

  React.useEffect(() => {
    if (mannequinImages.length <= 1) return;
    const interval = window.setInterval(() => {
      setCurrentMannequinIndex(
        (prev) => (prev + 1) % mannequinImages.length
      );
    }, 5000);
    return () => window.clearInterval(interval);
  }, [mannequinImages.length]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
      <PromoCountdownBanner />
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: { xs: 7, md: 10 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(135deg, rgba(0,0,0,0.45), rgba(0,0,0,0.2))',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' },
              gap: 4,
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  opacity: 0.9,
                }}
              >
                Bratan Family
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.4rem', md: '3.2rem' },
                  letterSpacing: 0.5,
                }}
              >
                {"Frères de coeur, marque d'honneur."}
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.92, maxWidth: 620 }}
              >
                L'élégance et la fierté prennent vie dans chaque pièce , confectionnée avec exigence en Belgique pour vous garantir une qualité irréprochable.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  component={Link}
                  to="/products"
                  sx={{ borderRadius: 999, px: 4 }}
                >
                  Découvrir la collection
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  component={Link}
                  to="/cart"
                  sx={{ borderRadius: 999, px: 3 }}
                >
                  Voir mon panier
                </Button>
              </Box>
            </Box>
            {mannequinImages.length > 0 && (
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  mt: { xs: 4, md: 0 },
                  height: { xs: 260, md: 320 },
                }}
              >
                <Box
                  component="img"
                  src={mannequinImages[currentMannequinIndex]}
                  alt="Bratan Family - look mannequin"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  loading="lazy"
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.15))',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={handlePrevMannequin}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.6)',
                        },
                      }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNextMannequin}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.6)',
                        },
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.contrastText', opacity: 0.9 }}
                  >
                    {currentMannequinIndex + 1}/{mannequinImages.length}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Catégories rapides */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {categories.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Parcourez par univers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {categories.map((cat) => {
                const count = products.filter((p) => p.category === cat).length;
                return (
                  <Chip
                    key={cat}
                    label={`${cat} (${count})`}
                    component={Link}
                    to="/products"
                    clickable
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Nouveautés */}
        {newProducts.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: 2,
              }}
            >
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                Nouveautés
              </Typography>
              <Button
                component={Link}
                to="/products"
                variant="text"
                size="small"
              >
                Voir tout
              </Button>
            </Box>

            {/* Desktop / tablette : grille classique */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: {
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              {newProducts.map((product) => (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 2,
                  }}
                >
                  <CardMedia
                    component="img"
                    height="220"
                    image={product.image}
                    alt={product.name}
                    loading="lazy"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getFinalPrice(product).toFixed(2)} €
                    </Typography>
                    <Chip
                      label="Nouveau"
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Box
              sx={{
                mt: 1,
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${10 + newCarouselProgress * 90}%`,
                    height: '100%',
                    borderRadius: 999,
                    bgcolor: 'primary.main',
                    transition: 'width 0.2s ease',
                  }}
                />
              </Box>
            </Box>

            {/* Mobile : carrousel horizontal */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                overflowX: 'auto',
                px: 1,
                py: 1,
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              }}
              onScroll={handleNewCarouselScroll}
            >
              {newProducts.map((product) => (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 2,
                    width: '100%',
                    maxWidth: 360,
                    flexShrink: 0,
                    mx: 1,
                    scrollSnapAlign: 'center',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={product.image}
                    alt={product.name}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 220,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getFinalPrice(product).toFixed(2)} €
                    </Typography>
                    <Chip
                      label="Nouveau"
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Promotions */}
        {promoProducts.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: 2,
              }}
            >
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                Nos meilleures offres
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Profitez des remises en cours
                </Typography>
                {allPromoProducts.length > 4 && (
                  <Button
                    component={Link}
                    to="/products"
                    variant="text"
                    size="small"
                  >
                    Voir tout
                  </Button>
                )}
              </Box>
            </Box>

            {/* Desktop / tablette : grille classique */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: {
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 3,
              }}
            >
              {promoProducts.map((product) => (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 2,
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={product.image}
                      alt={product.name}
                      loading="lazy"
                      sx={{ objectFit: 'cover' }}
                    />
                    {product.discount && product.discount > 0 && (
                      <Chip
                        label={`-${product.discount}%`}
                        color="error"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                        }}
                      />
                    )}
                  </Box>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {product.name}
                    </Typography>
                    {product.discount && product.discount > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {product.price.toFixed(2)} €
                      </Typography>
                    )}
                    <Typography variant="body1" color="primary">
                      {getFinalPrice(product).toFixed(2)} €
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Mobile : carrousel horizontal */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                overflowX: 'auto',
                px: 1,
                py: 1,
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              }}
              onScroll={handlePromoCarouselScroll}
            >
              {promoProducts.map((product) => (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 2,
                    minWidth: '75%',
                    maxWidth: 360,
                    flexShrink: 0,
                    mx: 1,
                    scrollSnapAlign: 'center',
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      image={product.image}
                      alt={product.name}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: 220,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {product.discount && product.discount > 0 && (
                      <Chip
                        label={`-${product.discount}%`}
                        color="error"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                        }}
                      />
                    )}
                  </Box>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {product.name}
                    </Typography>
                    {product.discount && product.discount > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {product.price.toFixed(2)} €
                      </Typography>
                    )}
                    <Typography variant="body1" color="primary">
                      {getFinalPrice(product).toFixed(2)} €
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Box
              sx={{
                mt: 1,
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${10 + promoCarouselProgress * 90}%`,
                    height: '100%',
                    borderRadius: 999,
                    bgcolor: 'primary.main',
                    transition: 'width 0.2s ease',
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Bloc "Pourquoi nous choisir ?" */}
        <Box
          sx={{
            mt: 2,
            mb: 8,
            p: { xs: 3, md: 4 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 1,
          }}
        >
          <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
            Pourquoi nous choisir ?
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Livraison rapide
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expédition sous 24/72h pour les articles en stock.
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ↩️ Retours faciles et S.A.V
              </Typography>
              <Typography variant="body2" color="text.secondary">
                14 jours pour changer d'avis et retourner vos articles.
                <br />
                Une équipe disponible pour vous accompagner avant et après l'achat.
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Paiement sécurisé
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Transactions protégées via une plateforme de paiement reconnue.
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                 🇧🇪 Qualité Belge
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Une marque créée avec passion, produite et conçu en Belgique avec sois.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
      <ScrollToTopButton />
    </Box>
  );
};

export default Home;
